// Sidebar.js - Wilout UI Brandbook Layout
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  CalendarCheck,
  FileText,
  BookOpen,
  Wrench,
  Users,
  Settings,
  Moon,
  Sun,
  ChevronDown,
  ChevronRight,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Smartphone,
  Upload,
  Archive,
  FileSpreadsheet,
  MessageSquare,
  List,
  Truck,
  Package,
  MoreHorizontal,
  Code,
  Monitor,
  TrendingUp,
  LifeBuoy,
  Target,
  Tag,
  Search,
  ClipboardList,
  Building2,
} from 'lucide-react';
import logo from '../images/logo.png';
import { useWilout } from '../context/WiloutContext';
import { useRoles } from '../context/RolesContext';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { getMyTaskCounts } from '../api/api';
import api from '../api/api';
import LanguageSwitcher from './LanguageSwitcher';
import EventSearchPalette from './EventSearchPalette';
import './Sidebar.css';

// Le raccourci affiché suit la plateforme (⌘K sur macOS, Ctrl K ailleurs)
const isMacOs = typeof navigator !== 'undefined'
  && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');

const Sidebar = ({ isCollapsed, onToggleCollapse, isMobileOpen, onCloseMobile }) => {
  const { t } = useTranslation();
  const { user, theme, toggleTheme, logout } = useWilout();
  const { getRoleByName } = useRoles();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [taskCounts, setTaskCounts] = useState({ assigned: 0, pool: 0 });
  const [adminNotifCount, setAdminNotifCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);

  // Raccourci global ⌘K / Ctrl+K pour ouvrir la recherche d'événements
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key?.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    onCloseMobile?.();
  }, [onCloseMobile]);

  const closeSearch = useCallback(() => setSearchOpen(false), []);

  // Récupérer les compteurs de tâches (assignées + pool)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const data = await getMyTaskCounts();
        if (!cancelled) setTaskCounts(data);
      } catch {
        // silently ignore
      }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  // Récupérer les notifications admin
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchAdminNotifs = async () => {
      try {
        const res = await api.get('/users/admin-notifications');
        if (!cancelled) setAdminNotifCount(res.data?.total || 0);
      } catch {
        // silently ignore
      }
    };
    fetchAdminNotifs();
    const interval = setInterval(fetchAdminNotifs, 120000); // refresh every 2min
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  // Récupérer les permissions du rôle de l'utilisateur
  const userPermissions = useMemo(() => {
    if (!user) return {};
    const role = getRoleByName(user.role);
    return role?.permissions || {};
  }, [user, getRoleByName]);

  // Build user submenu items based on dynamic permissions
  const userMenuItems = useMemo(() => {
    if (!user) return [];
    const items = [];

    // Accès factures basé sur la permission canAccessInvoices
    if (userPermissions.canAccessInvoices) {
      items.push({ path: '/documents/factures', icon: Upload, label: t('sidebar.submitInvoices') });
      items.push({ path: '/admin/factures', icon: Archive, label: t('sidebar.viewInvoices') });
    }

    return items;
  }, [user, userPermissions, t]);

  // Auto-expand menu based on current route
  useEffect(() => {
    if (location.pathname.includes('/evenements') ||
        location.pathname.includes('/completed-events') ||
        location.pathname.includes('/retex') ||
        location.pathname.includes('/organisateurs') ||
        location.pathname.includes('/events/')) {
      setExpandedMenus(prev => ({ ...prev, events: true }));
    }
    if (location.pathname.includes('/logistique') || location.pathname.includes('/terminals')) {
      setExpandedMenus(prev => ({ ...prev, logistique: true }));
    }
    if (location.pathname.includes('/tools')) {
      setExpandedMenus(prev => {
        const next = { ...prev, tools: true };
        if (location.pathname.includes('/tools/csv-') || location.pathname.includes('/tools/excel-')) {
          next['tools-converters'] = true;
        } else if (location.pathname.includes('/tools/order-')) {
          next['tools-analysis'] = true;
        }
        return next;
      });
    }
    if (location.pathname.includes('/commercials') || location.pathname.includes('/nouvel-evenement') || location.pathname.includes('/dashboard-commercial') || location.pathname.includes('/prospection')) {
      setExpandedMenus(prev => ({ ...prev, commercial: true }));
    }
    if (location.pathname.includes('/documents/factures') ||
        location.pathname.includes('/admin/factures')) {
      setUserMenuOpen(true);
    }
  }, [location.pathname]);

  const toggleMenu = useCallback((menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  }, []);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
  };

  // Navigation items avec permissions dynamiques, regroupés en 3 sections (brandbook)
  const navGroups = useMemo(() => [
    {
      key: 'pilotage',
      label: t('sidebar.groupPilotage', 'Pilotage'),
      items: [
        {
          type: 'link',
          path: '/',
          icon: LayoutDashboard,
          label: t('sidebar.dashboard'),
          exact: true
        },
        {
          type: 'group',
          key: 'events',
          icon: Calendar,
          label: t('sidebar.events'),
          children: [
            { path: '/evenements', icon: Calendar, label: t('sidebar.allEvents') },
            { path: '/completed-events', icon: CalendarCheck, label: t('sidebar.completedEvents'), visible: Boolean(userPermissions.canViewEvents || userPermissions.isAdmin) },
            { path: '/retex', icon: FileText, label: t('sidebar.allRetex'), visible: Boolean(userPermissions.canAccessRetex || userPermissions.isAdmin) },
            { path: '/organisateurs', icon: Building2, label: t('sidebar.organizers', 'Organisateurs'), visible: Boolean(userPermissions.canViewEvents || userPermissions.isAdmin) }
          ]
        },
        {
          type: 'group',
          key: 'logistique',
          icon: Truck,
          label: t('sidebar.logistics'),
          visible: userPermissions.canAccessLogistique || userPermissions.canAccessTerminals || userPermissions.canAccessSchedule || userPermissions.isAdmin,
          children: [
            { path: '/logistique/stock', icon: Package, label: t('sidebar.stock'), visible: userPermissions.canAccessLogistique || userPermissions.isAdmin },
            { path: '/logistique/planning', icon: CalendarCheck, label: t('sidebar.planning'), visible: userPermissions.canAccessLogistique || userPermissions.canAccessSchedule || userPermissions.isAdmin },
            ...(userPermissions.canAccessTerminals
              ? [
                  { path: '/terminals', icon: Smartphone, label: t('sidebar.terminals') },
                ]
              : []),
            // Le MDM vit dans la page Terminaux (onglet « Parc MDM ») : entrée à part seulement pour
            // qui a le MDM sans l'accès aux terminaux.
            ...((userPermissions.canAccessMdmSunmi || userPermissions.isAdmin) && !userPermissions.canAccessTerminals
              ? [{ path: '/terminals/mdm', icon: Monitor, label: 'MDM Sunmi' }]
              : []),
          ]
        }
      ]
    },
    {
      key: 'ressources',
      label: t('sidebar.groupRessources', 'Ressources'),
      items: [
        { type: 'link', path: '/ressources', icon: BookOpen, label: t('sidebar.wiki'), visible: Boolean(userPermissions.canViewResources || userPermissions.isAdmin) },
        ...((userPermissions.canViewPromoCodes || userPermissions.isAdmin)
          ? [{ type: 'link', path: '/wiki/codes-promo', icon: Tag, label: t('sidebar.promoCodes') }]
          : []),
        {
          type: 'group',
          key: 'tools',
          icon: Wrench,
          label: t('sidebar.tools'),
          visible: userPermissions.canAccessTools || userPermissions.isAdmin,
          children: [
            {
              type: 'subgroup', key: 'tools-converters', label: t('sidebar.toolsConverters'),
              children: [
                { path: '/tools/csv-converter', icon: FileText, label: t('sidebar.csvConverter') },
                { path: '/tools/accreditation', icon: ClipboardList, label: t('sidebar.accreditation') },
                { path: '/tools/excel-compiler', icon: FileSpreadsheet, label: t('sidebar.excelCompiler') },
              ]
            },
            // L'analyse cashless n'est plus une page autonome : elle s'ouvre depuis la fiche événement.
            ...(userPermissions.canAccessOrderSearch || userPermissions.isAdmin
              ? [{
                type: 'subgroup', key: 'tools-analysis', label: t('sidebar.toolsAnalysis'),
                children: [
                  { path: '/tools/order-lookup', icon: FileText, label: t('sidebar.orderLookup', 'Recherche Order') },
                ]
              }]
              : []),
          ]
        },
        ...((userPermissions.canAccessSupport || userPermissions.isAdmin)
          ? [{ type: 'link', path: '/support', icon: LifeBuoy, label: 'Support' }]
          : [])
      ]
    },
    {
      key: 'entreprise',
      label: t('sidebar.groupEntreprise', 'Entreprise'),
      items: [
        {
          type: 'group',
          key: 'commercial',
          icon: Users,
          label: 'Commercial',
          visible: userPermissions.canAccessOnboarding || userPermissions.canAccessSalesDashboard || userPermissions.canAccessProspection || userPermissions.canLoanDemoDevices || userPermissions.isAdmin,
          children: [
            {
              path: user?.teamAutonomous ? '/nouvel-evenement' : '/commercials',
              icon: Users,
              label: user?.teamAutonomous ? t('sidebar.newEvent') : t('sidebar.onboarding'),
              visible: userPermissions.canAccessOnboarding || userPermissions.isAdmin
            },
            {
              path: '/dashboard-commercial',
              icon: TrendingUp,
              label: 'Dashboard',
              visible: userPermissions.canAccessSalesDashboard || userPermissions.isAdmin
            },
            {
              path: '/dashboard-commercial/settings',
              icon: Settings,
              label: 'Paramètres',
              visible: userPermissions.canAccessSalesDashboard || userPermissions.isAdmin
            },
            {
              path: '/prospection',
              icon: Target,
              label: 'Prospection',
              visible: userPermissions.canAccessProspection || userPermissions.isAdmin
            },
            {
              path: '/demo-pads',
              icon: Smartphone,
              label: t('sidebar.demoPads'),
              visible: Boolean(userPermissions.canLoanDemoDevices || userPermissions.canEditTerminals || userPermissions.isAdmin)
            }
          ]
        },
        {
          type: 'external',
          key: 'suggestions',
          path: `${(typeof window !== 'undefined' && window.env && window.env.kubeControlUrl) || process.env.REACT_APP_KUBE_CONTROL_URL || 'https://kube-control.prod.wilout-internal.com'}/suggestions`,
          icon: MessageSquare,
          label: t('sidebar.suggestions'),
          visible: userPermissions.canAccessSuggestions || userPermissions.isAdmin
        },
        {
          type: 'link',
          path: '/admin',
          icon: Settings,
          label: t('sidebar.admin'),
          visible: userPermissions.canAccessAdmin,
          badge: adminNotifCount > 0 ? adminNotifCount : null
        }
      ]
    }
  ], [userPermissions, t, adminNotifCount, user?.teamAutonomous]);

  const renderNavItem = (item) => {
    // Check role restrictions (legacy)
    // Check visibility based on dynamic permissions
    if (item.visible === false) return null;

    if (item.type === 'link') {
      return (
        <li key={item.path} className="sidebar-nav-item">
          <Link
            to={item.path}
            className={`sidebar-nav-link ${isActive(item.path) ? 'active' : ''}`}
            title={isCollapsed ? item.label : undefined}
            onClick={onCloseMobile}
          >
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <item.icon size={20} className="sidebar-nav-icon" />
              {item.badge && isCollapsed && (
                <span className="sidebar-admin-badge">{item.badge > 99 ? '99+' : item.badge}</span>
              )}
            </span>
            {!isCollapsed && (
              <>
                <span className="sidebar-nav-label">{item.label}</span>
                {item.badge && (
                  <span className="sidebar-admin-badge-inline">{item.badge > 99 ? '99+' : item.badge}</span>
                )}
              </>
            )}
          </Link>
        </li>
      );
    }

    if (item.type === 'external') {
      return (
        <li key={item.key || item.path} className="sidebar-nav-item">
          <a
            href={item.path}
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar-nav-link"
            title={isCollapsed ? item.label : undefined}
            onClick={onCloseMobile}
          >
            <item.icon size={20} className="sidebar-nav-icon" />
            {!isCollapsed && <span className="sidebar-nav-label">{item.label} ↗</span>}
          </a>
        </li>
      );
    }

    if (item.type === 'group') {
      const isExpanded = expandedMenus[item.key];
      const visibleChildren = item.children.filter(child => child.visible !== false);
      const hasActiveChild = visibleChildren.some(child => {
        if (child.type === 'subgroup') return child.children.some(c => c.path && isActive(c.path));
        return child.path && isActive(child.path);
      });

      if (visibleChildren.length === 0) return null;

      return (
        <li key={item.key} className="sidebar-nav-item has-children">
          <button
            className={`sidebar-nav-link ${hasActiveChild ? 'active' : ''}`}
            onClick={() => !isCollapsed && toggleMenu(item.key)}
            title={isCollapsed ? item.label : undefined}
          >
            <item.icon size={20} className="sidebar-nav-icon" />
            {!isCollapsed && (
              <>
                <span className="sidebar-nav-label">{item.label}</span>
                <span className="sidebar-nav-chevron">
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </span>
              </>
            )}
          </button>

          {/* Submenu when sidebar is expanded */}
          {!isCollapsed && isExpanded && (
            <ul className="sidebar-submenu">
              {visibleChildren.map(child => {
                if (child.type === 'subgroup') {
                  const isSubExpanded = expandedMenus[child.key];
                  const hasSubActive = child.children.some(c => isActive(c.path));
                  return (
                    <li key={child.key} className="sidebar-subgroup">
                      <button
                        className={`sidebar-subgroup-toggle ${hasSubActive ? 'active' : ''}`}
                        onClick={() => toggleMenu(child.key)}
                      >
                        <span>{child.label}</span>
                        <span className="sidebar-subgroup-chevron">
                          {isSubExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </span>
                      </button>
                      {isSubExpanded && (
                        <ul className="sidebar-subgroup-items">
                          {child.children.map(link => (
                            <li key={link.path} className="sidebar-submenu-item">
                              <Link
                                to={link.path}
                                className={`sidebar-submenu-link ${isActive(link.path) ? 'active' : ''}`}
                                onClick={onCloseMobile}
                              >
                                <span className={`sidebar-submenu-dot ${isActive(link.path) ? 'active' : ''}`} />
                                <span className="sidebar-submenu-label">{link.label}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }
                return (
                  <li key={child.path} className="sidebar-submenu-item">
                    <Link
                      to={child.path}
                      className={`sidebar-submenu-link ${isActive(child.path) ? 'active' : ''}`}
                      onClick={onCloseMobile}
                    >
                      <span className={`sidebar-submenu-dot ${isActive(child.path) ? 'active' : ''}`} />
                      <span className="sidebar-submenu-label">{child.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Flyout submenu when sidebar is collapsed */}
          {isCollapsed && (
            <div className="sidebar-submenu-flyout">
              <div className="sidebar-submenu-flyout-header">
                {item.label}
              </div>
              {visibleChildren.map(child => {
                if (child.type === 'subgroup') {
                  return (
                    <div key={child.key}>
                      <div className="sidebar-flyout-section">{child.label}</div>
                      {child.children.map(link => (
                        <Link
                          key={link.path}
                          to={link.path}
                          className={`sidebar-submenu-link ${isActive(link.path) ? 'active' : ''}`}
                        >
                          <link.icon size={16} className="sidebar-submenu-icon" />
                          <span className="sidebar-submenu-label">{link.label}</span>
                        </Link>
                      ))}
                    </div>
                  );
                }
                return (
                  <Link
                    key={child.path}
                    to={child.path}
                    className={`sidebar-submenu-link ${isActive(child.path) ? 'active' : ''}`}
                  >
                    <child.icon size={16} className="sidebar-submenu-icon" />
                    <span className="sidebar-submenu-label">{child.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </li>
      );
    }

    return null;
  };

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'open' : ''}`}>
      {/* Logo Section */}
      <div className="sidebar-header">
        {/* Logo - cliquable pour ouvrir la sidebar quand collapsed */}
        {isCollapsed ? (
          <button
            className="sidebar-logo-btn"
            onClick={onToggleCollapse}
            title={t('sidebar.openMenu')}
            aria-label={t('sidebar.openMenu')}
          >
            <img src={logo} alt="Wilout" className="sidebar-logo-img" />
          </button>
        ) : (
          <Link to="/" className="sidebar-logo">
            <img src={logo} alt="Wilout" className="sidebar-logo-img" />
          </Link>
        )}

        {/* Toggle button - visible uniquement quand sidebar expanded */}
        {!isCollapsed && (
          <button
            className="sidebar-toggle"
            onClick={isMobileOpen ? onCloseMobile : onToggleCollapse}
            title={t('sidebar.closeMenu')}
            aria-label={t('sidebar.closeMenu')}
          >
            <PanelLeftClose size={20} />
          </button>
        )}
      </div>

      {/* Recherche d'événements (palette ⌘K) */}
      <div className="sidebar-search-section">
        <button
          type="button"
          className="sidebar-search-btn"
          title={t('sidebar.search', 'Rechercher')}
          onClick={openSearch}
        >
          <Search size={16} />
          {!isCollapsed && (
            <>
              <span className="sidebar-search-label">{t('sidebar.search', 'Rechercher')}</span>
              <span className="sidebar-search-kbd">{isMacOs ? '⌘K' : 'Ctrl K'}</span>
            </>
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {navGroups.map(group => {
          const renderedItems = group.items.map(renderNavItem);
          if (renderedItems.every(el => el === null)) return null;
          return (
            <div key={group.key} className="sidebar-nav-group">
              {!isCollapsed && <div className="sidebar-nav-group-label">{group.label}</div>}
              {isCollapsed && <div className="sidebar-nav-group-divider" />}
              <ul className="sidebar-nav-list">
                {renderedItems}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Bottom Actions */}
      <div className="sidebar-footer">
        <div className="sidebar-footer-utils">
          <button
            className="sidebar-footer-btn"
            onClick={toggleTheme}
            title={theme === 'dark' ? t('sidebar.lightMode') : t('sidebar.darkMode')}
            aria-label={theme === 'dark' ? t('sidebar.lightMode') : t('sidebar.darkMode')}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <LanguageSwitcher collapsed={isCollapsed} />
          {isCollapsed && (
            <button
              className="sidebar-footer-btn logout"
              onClick={handleLogout}
              title={t('sidebar.logout')}
              aria-label={t('sidebar.logout')}
            >
              <LogOut size={18} />
            </button>
          )}
        </div>

        {user && !isCollapsed && (
          <div className="sidebar-user-row">
            <button
              className={`sidebar-user ${userMenuOpen ? 'open' : ''}`}
              onClick={() => setUserMenuOpen(prev => !prev)}
            >
              <div className="sidebar-user-avatar" style={{ position: 'relative' }}>
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                {taskCounts.assigned > 0 && (
                  <span className="sidebar-badge">{taskCounts.assigned}</span>
                )}
              </div>
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">
                  {user.name || user.email}
                  {taskCounts.pool > 0 && (
                    <span className="sidebar-badge-pool" title={t('sidebar.availableTasks')}>
                      +{taskCounts.pool}
                    </span>
                  )}
                </span>
                <span className="sidebar-user-role">{user.role}</span>
              </div>
              {userMenuItems.length > 0 && (
                <span className="sidebar-user-chevron">
                  {userMenuOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              )}
            </button>
            <button
              className="sidebar-footer-btn logout"
              onClick={handleLogout}
              title={t('sidebar.logout')}
              aria-label={t('sidebar.logout')}
            >
              <LogOut size={16} />
            </button>
          </div>
        )}

        {userMenuOpen && !isCollapsed && userMenuItems.length > 0 && (
          <ul className="sidebar-user-submenu">
            {userMenuItems.map(item => (
              <li key={item.path} className="sidebar-submenu-item">
                <Link
                  to={item.path}
                  className={`sidebar-submenu-link ${isActive(item.path) ? 'active' : ''}`}
                >
                  <span className={`sidebar-submenu-dot ${isActive(item.path) ? 'active' : ''}`} />
                  <span className="sidebar-submenu-label">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {user && isCollapsed && (
          <div className="sidebar-user-collapsed has-children">
            <div className="sidebar-user-avatar" style={{ position: 'relative' }}>
              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              {(taskCounts.assigned + taskCounts.pool) > 0 && (
                <span className="sidebar-badge">{taskCounts.assigned + taskCounts.pool}</span>
              )}
            </div>
            {userMenuItems.length > 0 && (
              <div className="sidebar-submenu-flyout user-flyout footer-flyout">
                <div className="sidebar-submenu-flyout-header">
                  {user.name || user.email}
                </div>
                {userMenuItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-submenu-link ${isActive(item.path) ? 'active' : ''}`}
                  >
                    <span className={`sidebar-submenu-dot ${isActive(item.path) ? 'active' : ''}`} />
                    <span className="sidebar-submenu-label">{item.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <EventSearchPalette open={searchOpen} onClose={closeSearch} />
    </aside>
  );
};

export default Sidebar;
